import React, { useEffect, useRef, useState } from "react";
import { AlertTriangle, ArrowRight } from "lucide-react";
import { useLanguage } from "@/context/LanguageContext";

const DefinitionsSection = () => {
  const { t } = useLanguage();
  const [isVisible, setIsVisible] = useState(false);
  const [expandedIdx, setExpandedIdx] = useState(null);
  const sectionRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => entry.isIntersecting && setIsVisible(true),
      { threshold: 0.1 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  const items = t("definitions.items") || [];

  return (
    <section id="definitions" ref={sectionRef} className="py-20 bg-white">
      <div className="max-w-7xl mx-auto px-6">
        <div
          className={`mb-6 transition-all duration-700 ${
            isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"
          }`}
        >
          <h2 className="text-3xl md:text-4xl font-bold text-[#1a1a2e] mb-4">
            {t("definitions.title")}
          </h2>
          <div className="w-20 h-1 bg-[#E8792B] rounded-full mb-6" />
        </div>

        <p
          className={`text-gray-600 text-base leading-relaxed max-w-4xl mb-12 transition-all duration-700 delay-200 ${
            isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"
          }`}
        >
          {t("definitions.intro")}
        </p>

        {/* GXR-style cards */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5 lg:gap-6">
          {Array.isArray(items) &&
            items.map((def, index) => {
              const num = String(index + 1).padStart(2, "0");
              const expanded = expandedIdx === index;
              return (
                <div
                  key={index}
                  onClick={() => setExpandedIdx(expanded ? null : index)}
                  className={`group relative overflow-hidden p-8 lg:p-9 bg-white border border-gray-200 hover:shadow-[0_8px_30px_rgba(0,0,0,0.06)] cursor-pointer transition-all duration-500 ${
                    isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"
                  }`}
                  style={{
                    transitionDelay: `${index * 80}ms`,
                    borderColor: expanded ? def.color : undefined,
                  }}
                >
                  {/* Large faded number watermark */}
                  <div
                    className="absolute -bottom-8 -right-2 font-black text-[140px] lg:text-[180px] leading-none pointer-events-none select-none"
                    style={{ color: def.color, opacity: 0.07 }}
                  >
                    {num}
                  </div>

                  {/* Top hairline accent that grows on hover */}
                  <span
                    className="absolute top-0 left-0 h-px w-12 transition-all duration-500 group-hover:w-full"
                    style={{ backgroundColor: def.color }}
                  />

                  {/* Square icon tile with the definition's color */}
                  <div
                    className="relative inline-flex items-center justify-center h-12 w-12 transition-all duration-300 group-hover:shadow-[0_0_24px_rgba(0,0,0,0.12)]"
                    style={{
                      backgroundColor: `${def.color}10`,
                      border: `1px solid ${def.color}66`,
                    }}
                  >
                    <AlertTriangle className="h-5 w-5" style={{ color: def.color }} strokeWidth={2} />
                  </div>

                  <div
                    className="relative mt-8 text-[11px] font-bold tracking-[0.3em]"
                    style={{ color: def.color, opacity: 0.75 }}
                  >
                    · {num} ·
                  </div>

                  <h3 className="relative mt-4 text-[20px] lg:text-[22px] font-bold leading-[1.2] text-[#1a1a2e] tracking-tight">
                    {def.title}
                  </h3>

                  <p
                    className={`relative mt-4 text-gray-600 text-[14px] leading-relaxed max-w-[36ch] transition-all duration-300 ${
                      expanded ? "" : "line-clamp-3"
                    }`}
                  >
                    {def.text}
                  </p>

                  {/* Read more / Hide control */}
                  <div className="relative mt-6 flex items-center gap-2 text-[10px] uppercase tracking-[0.24em] font-bold transition-all duration-300" style={{ color: def.color }}>
                    <span
                      className="h-px w-6 transition-all duration-300 group-hover:w-10"
                      style={{ backgroundColor: def.color }}
                    />
                    <span>
                      {expanded ? t("definitions.hide") : t("definitions.readMore")}
                    </span>
                    <ArrowRight
                      className={`h-3.5 w-3.5 transition-transform duration-300 ${
                        expanded ? "rotate-90" : "group-hover:translate-x-1"
                      }`}
                    />
                  </div>
                </div>
              );
            })}
        </div>
      </div>
    </section>
  );
};

export default DefinitionsSection;
